import type {
  FormattedMemberTransaction,
  FormattedMemberTransactions,
  FormattedMemberTransactionsResponse,
  MemberTransaction,
  MemberTransactionsAPIResponse
} from '@library/types/members/memberTransactions';
import {
  getDateTime,
  getDollarAmount,
  getFormattedDateTime
} from '@bo-utils/members/memberCommon';
import { type ComboboxOption } from '@library/components/selects';
import { FETCH_TRANSACTIONS_PER_PAGE_COUNT } from '@bo-constants/member/members';
import type { GenericFilterTypesApiResponse } from '@library/types/utils';
import { values } from 'ramda';

export const formatTrasactionType = ({
  transactionType
}: {
  transactionType: string | undefined;
}) =>
  transactionType
    ? transactionType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

export const transformTransactionTypes = ({
  response
}: {
  response: GenericFilterTypesApiResponse | undefined;
}): Array<ComboboxOption> => {
  if (!response) {
    return [];
  }

  const transactionTypeOptions: Array<ComboboxOption> = values(response)
    .sort()
    .map((transactionType) => ({
      label: formatTrasactionType({ transactionType }),
      value: transactionType
    }));

  return transactionTypeOptions;
};

export const getMemberTransaction = ({
  memberTransaction
}: {
  memberTransaction: MemberTransaction;
}): FormattedMemberTransaction => {
  const {
    id: transactionId,
    attributes: {
      amount_cents: amountCents,
      created_at: createdAt,
      credit_at_time_cents: creditAtTimeCents,
      note,
      object_type: objectType,
      object_id: objectId,
      promo_at_time_cents: promoAtTimeCents,
      reason,
      transaction_type: transactionType
    }
  } = memberTransaction;

  const promoId =
    objectType === 'Promotion' && objectId ? objectId.toString() : '';

  const newWagerId =
    objectType === 'NewWager' && objectId ? objectId.toString() : '';

  const { formattedDate: createdAtDate, formattedTime: createdAtTime } =
    getFormattedDateTime({
      dateTime: getDateTime({ dateString: createdAt || '' })
    });

  return {
    createdAtDate,
    createdAtTime,
    creditAtTimeAmount: getDollarAmount({
      amountInCents: creditAtTimeCents
    }),
    note: note || '',
    promoId,
    newWagerId,
    promoAtTimeAmount: getDollarAmount({ amountInCents: promoAtTimeCents }),
    reason: reason || '',
    transactionAmount: getDollarAmount({ amountInCents: amountCents }),
    transactionId,
    transactionType
  };
};

export const transformMemberTransactions = ({
  page,
  perPage,
  response
}: {
  page: number;
  perPage: number;
  response: MemberTransactionsAPIResponse;
}): FormattedMemberTransactionsResponse => {
  const { data } = response;

  if (!data) {
    return {
      memberTransactions: [],
      totalPages: 1
    };
  }

  const memberTransactions: FormattedMemberTransactions = data.map(
    (memberTransaction) => getMemberTransaction({ memberTransaction })
  );

  return {
    memberTransactions,
    totalPages:
      memberTransactions.length < (perPage || FETCH_TRANSACTIONS_PER_PAGE_COUNT)
        ? page
        : page + 1
  };
};
